import React, { useCallback, useMemo } from 'react';
import {
  Box, Typography, Switch, Select, MenuItem, InputBase, Checkbox, FormControlLabel, useTheme,
} from '@mui/material';
import { useFormPalette } from '../pages/forms/DynamicFormPrimitives';
import { NODE_UI } from '../NodeUI/nodeUiStyles';

const DEFAULT_FLOW_CONFIG = Object.freeze({
  title: '',
  branding: {},
  allowBack: true,
});

function labelStyle(palette) {
  return {
    fontSize: NODE_UI.fontSize.label,
    fontWeight: NODE_UI.fontWeight.label,
    color: palette.textSecondary,
    lineHeight: 1.4,
    userSelect: 'none',
  };
}

function TextInput({ value, onChange, placeholder, palette }) {
  return (
    <InputBase
      value={value || ''}
      onChange={(e) => onChange(e.target.value)}
      placeholder={placeholder}
      fullWidth
      sx={{
        fontFamily: 'inherit',
        fontSize: NODE_UI.fontSize.value,
        color: palette.textPrimary,
        border: `1px solid ${palette.border}`,
        borderRadius: NODE_UI.borderRadiusSm,
        px: '8px',
        py: '5px',
        '&:hover': { borderColor: palette.textSecondary },
        '&.Mui-focused': { borderColor: palette.textPrimary },
      }}
    />
  );
}

/**
 * Mutually-exclusive flow-membership controls for any v2 form-like.
 *
 * Three states on the wire:
 *   - {@code flowEntryId === componentId} + {@code flowConfig != null} → this form is a flow entry
 *   - {@code flowEntryId === somewhereUpstream} + {@code flowConfig == null} → member of that flow
 *   - {@code flowEntryId == null} + {@code flowConfig == null} → standalone
 *
 * Props:
 *   componentId          — this form's id (needed to set flowEntryId when starting a flow)
 *   componentData        — { flowEntryId, flowConfig }
 *   upstreamFlowEntries  — [{ componentId, componentName, flowTitle }, …]
 *   onChange({flowEntryId, flowConfig}) — single setter for both fields
 */
export default function FlowSettingsBlock({
  componentId,
  componentName,
  componentData,
  upstreamFlowEntries,
  onChange,
}) {
  const palette = useFormPalette();
  const theme = useTheme();

  const flowEntryId = componentData?.flowEntryId ?? null;
  const flowConfig = componentData?.flowConfig ?? null;

  const isFlowEntry = flowEntryId && flowEntryId === componentId && flowConfig != null;
  const isAttached = flowEntryId && flowEntryId !== componentId;

  const handleStartFlowToggle = useCallback(
    (e) => {
      if (e.target.checked) {
        // Seed the flow title with the component's stepperTitle or name so the "required"
        // constraint is satisfied out of the gate. The JSON schema enforces
        // {@code minLength: 1} on flowConfig.title, so saving with an empty title will be
        // rejected server-side — pre-seeding prevents that common footgun.
        const seededTitle =
          componentData?.stepperTitle
            || componentName
            || 'Untitled form flow';
        onChange({
          flowEntryId: componentId,
          flowConfig: { ...DEFAULT_FLOW_CONFIG, title: seededTitle },
        });
      } else {
        onChange({ flowEntryId: null, flowConfig: null });
      }
    },
    [onChange, componentId, componentName, componentData],
  );

  const handleAttachChange = useCallback(
    (value) => {
      if (!value) {
        onChange({ flowEntryId: null, flowConfig: null });
      } else {
        onChange({ flowEntryId: value, flowConfig: null });
      }
    },
    [onChange],
  );

  const updateFlowConfig = useCallback(
    (patch) => {
      onChange({
        flowEntryId: componentId,
        flowConfig: { ...DEFAULT_FLOW_CONFIG, ...(flowConfig || {}), ...patch },
      });
    },
    [onChange, componentId, flowConfig],
  );

  const updateBranding = useCallback(
    (patch) => {
      updateFlowConfig({
        branding: { ...(flowConfig?.branding || {}), ...patch },
      });
    },
    [updateFlowConfig, flowConfig],
  );

  const upstreamOptions = useMemo(() => upstreamFlowEntries || [], [upstreamFlowEntries]);
  const noUpstreamAvailable = upstreamOptions.length === 0;

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
      {/* Start-a-flow toggle */}
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '12px' }}>
        <Box>
          <Typography sx={labelStyle(palette)}>Start a new form flow here</Typography>
          <Typography sx={{ fontSize: NODE_UI.fontSize.caption, color: palette.textSecondary, opacity: 0.7 }}>
            Makes this step the entry of a multi-step flow.
          </Typography>
        </Box>
        <Switch
          checked={!!isFlowEntry}
          onChange={handleStartFlowToggle}
          disabled={isAttached}
        />
      </Box>

      {/* Attach-to picker (disabled when isFlowEntry) */}
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
        <Typography sx={labelStyle(palette)}>Attach to an existing form flow</Typography>
        <Select
          value={isAttached ? flowEntryId : ''}
          onChange={(e) => handleAttachChange(e.target.value)}
          size="small"
          displayEmpty
          disabled={isFlowEntry || noUpstreamAvailable}
          sx={{
            fontFamily: 'inherit',
            fontSize: NODE_UI.fontSize.value,
            color: palette.textPrimary,
            backgroundColor: theme.palette.background.default,
            borderRadius: NODE_UI.borderRadiusSm,
            '& .MuiOutlinedInput-notchedOutline': { borderColor: palette.border },
            '& .MuiSelect-select': { padding: NODE_UI.input.padding },
          }}
        >
          <MenuItem value="">
            {noUpstreamAvailable ? 'No upstream flows available' : 'Standalone (not attached)'}
          </MenuItem>
          {upstreamOptions.map((o) => (
            <MenuItem key={o.componentId} value={o.componentId}>
              {o.flowTitle
                ? `${o.flowTitle} · ${o.componentName}`
                : o.componentName}
            </MenuItem>
          ))}
        </Select>
      </Box>

      {/* Flow-entry inline config */}
      {isFlowEntry && (
        <Box sx={{
          display: 'flex', flexDirection: 'column', gap: '10px',
          p: '10px', borderRadius: NODE_UI.borderRadiusSm,
          backgroundColor: palette.surfaceSubtle, border: `1px solid ${palette.border}`,
        }}>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
            <Typography sx={labelStyle(palette)}>Flow title (required)</Typography>
            <TextInput
              value={flowConfig?.title || ''}
              onChange={(v) => updateFlowConfig({ title: v })}
              placeholder="e.g. Onboarding checklist"
              palette={palette}
            />
          </Box>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
            <Typography sx={labelStyle(palette)}>Logo URL</Typography>
            <TextInput
              value={flowConfig?.branding?.logo || ''}
              onChange={(v) => updateBranding({ logo: v })}
              placeholder="https://example.com/logo.svg"
              palette={palette}
            />
          </Box>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
            <Typography sx={labelStyle(palette)}>Cover image URL</Typography>
            <TextInput
              value={flowConfig?.branding?.coverImage || ''}
              onChange={(v) => updateBranding({ coverImage: v })}
              placeholder="https://example.com/cover.jpg"
              palette={palette}
            />
          </Box>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
            <Typography sx={labelStyle(palette)}>Accent color</Typography>
            <TextInput
              value={flowConfig?.branding?.accentColor || ''}
              onChange={(v) => updateBranding({ accentColor: v })}
              placeholder="#2670ff"
              palette={palette}
            />
          </Box>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: '2px' }}>
            <FormControlLabel
              control={
                <Checkbox
                  checked={flowConfig?.allowBack !== false}
                  onChange={(e) => updateFlowConfig({ allowBack: e.target.checked })}
                  size="small"
                />
              }
              label={
                <Typography sx={{ fontSize: NODE_UI.fontSize.value, color: palette.textPrimary }}>
                  Allow respondents to go back to previous steps
                </Typography>
              }
            />
          </Box>
          {!(flowConfig?.title || '').trim() && (
            <Box sx={{
              px: '8px', py: '6px', borderRadius: NODE_UI.borderRadiusSm,
              backgroundColor: palette.errorBg || '#fee',
              border: `1px solid ${theme.palette.error.main}33`,
            }}>
              <Typography sx={{ fontSize: NODE_UI.fontSize.caption, color: theme.palette.error.main, fontWeight: 500 }}>
                Flow title is required. Saves with an empty title will be rejected by the
                server.
              </Typography>
            </Box>
          )}
        </Box>
      )}
    </Box>
  );
}
